import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { BadgeCheck, CalendarCheck, CalendarRange, MapPin, Plus } from 'lucide-react';
import { useLanguage, useSeo } from '../i18n/LanguageContext';
import { IMAGES } from '../data/images';
import { ArrowButton, Eyebrow, Marquee, MaskedLines, Reveal, SectionHead, EASE } from '../components/bits';
import BeforeAfter from '../components/BeforeAfter';
import FinalCTA from '../components/FinalCTA';

const VALUE_ICONS = [BadgeCheck, CalendarCheck, CalendarRange, MapPin];
const HOME_BA = { 'window-cleaning': 'window', 'bin-cleaning': 'bins', 'lawn-care': 'lawn', 'gutter-cleaning': 'gutter' };

function Hero() {
  const { t } = useLanguage();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink" data-testid="hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt={h.alt}
          fetchPriority="high"
          initial={{ scale: 1.12, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: EASE }}
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-40 sm:px-12 lg:px-20">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6, ease: EASE }}
          className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C6A15B] sm:text-xs"
        >
          <span className="h-px w-8 bg-[#C6A15B]" aria-hidden="true" />
          {h.eyebrow}
        </motion.p>
        <MaskedLines
          as="h1"
          lines={[h.line1, h.line2]}
          delay={0.45}
          className="mt-6 font-display text-5xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-7xl lg:text-8xl"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6, ease: EASE }}
          className="mt-7 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          {h.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <ArrowButton to="/contact" variant="gold" dataTestId="hero-quote-button">
            {h.ctaPrimary}
          </ArrowButton>
          <ArrowButton to="/services" variant="outlineWhite" dataTestId="hero-services-button">
            {h.ctaSecondary}
          </ArrowButton>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50"
          data-testid="hero-strip"
        >
          {h.strip}
        </motion.p>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-6 hidden items-center gap-3 sm:right-12 lg:flex lg:right-20"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{h.scroll}</span>
        <span className="h-10 w-px bg-gradient-to-b from-[#C6A15B] to-transparent" />
      </motion.div>
    </section>
  );
}

function ValueStrip() {
  const { t } = useLanguage();
  return (
    <section className="noise-bg bg-cream" data-testid="value-strip">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-y-10 px-6 py-16 sm:grid-cols-2 sm:px-12 lg:grid-cols-4 lg:gap-x-10 lg:py-20 lg:px-20">
        {t.values.items.map((v, i) => {
          const Icon = VALUE_ICONS[i];
          return (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="border-l border-forest/15 pl-6">
                <Icon className="h-5 w-5 text-forest" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-4 font-display text-sm font-extrabold uppercase tracking-[0.18em] text-ink">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{v.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function ServicesSection() {
  const { t } = useLanguage();
  const s = t.services;
  return (
    <section className="bg-white py-20 md:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <SectionHead eyebrow={s.eyebrow} lines={[s.heading]} className="lg:col-span-7" />
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{s.sub}</p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {s.items.map((svc, i) => (
            <Reveal key={svc.id} delay={(i % 3) * 0.08}>
              <div className="group" data-testid={`service-card-${svc.id}`}>
                {HOME_BA[svc.id] ? (
                  <BeforeAfter
                    beforeImage={IMAGES[`${HOME_BA[svc.id]}Before`]}
                    afterImage={IMAGES[svc.img]}
                    alt={svc.alt}
                    labels={s.beforeAfter}
                    aspectClass="aspect-[4/3]"
                  />
                ) : (
                  <Link to={`/services#${svc.id}`} className="block overflow-hidden" tabIndex={-1} aria-hidden="true">
                    <img
                      src={IMAGES[svc.img]}
                      alt=""
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </Link>
                )}
                <Link
                  to={`/services#${svc.id}`}
                  className="block border-t-2 border-transparent pt-5 transition-colors duration-300 group-hover:border-forest"
                >
                  <span className="font-mono text-[11px] font-semibold tracking-[0.3em] text-[#C6A15B]">
                    0{i + 1}
                  </span>
                  <h3 className="mt-2 font-display text-lg font-extrabold uppercase tracking-tight text-ink">
                    {svc.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{svc.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-forest">
                    {s.learnMore}
                    <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
                  </span>
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedLawn() {
  const { t } = useLanguage();
  const f = t.featured;
  return (
    <section className="noise-bg bg-cream py-20 md:py-32" data-testid="featured-lawn">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
        <Reveal className="lg:col-span-6">
          <div className="overflow-hidden">
            <motion.img
              src={IMAGES.featuredLawn}
              alt={f.alt}
              loading="lazy"
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, ease: EASE }}
              className="aspect-[4/3] w-full object-cover lg:aspect-[5/4]"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-6">
          <Eyebrow>{f.eyebrow}</Eyebrow>
          <MaskedLines
            as="h2"
            lines={[f.line1, f.line2]}
            inView
            delay={0.1}
            className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-7 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{f.body}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <ArrowButton to="/contact" variant="green" dataTestId="featured-quote-button" className="mt-9">
              {f.cta}
            </ArrowButton>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Philosophy() {
  const { t } = useLanguage();
  const p = t.philosophy;
  return (
    <section className="bg-ink py-20 md:py-32" data-testid="philosophy">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Eyebrow dark>{p.eyebrow}</Eyebrow>
        <MaskedLines
          as="h2"
          lines={[p.line1, p.line2]}
          inView
          delay={0.1}
          className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
        />
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{p.body}</p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-10 border-t border-white/15 pt-12 md:grid-cols-3">
          {p.points.map((pt, i) => (
            <Reveal key={pt.n} delay={i * 0.1}>
              <span className="font-mono text-sm font-semibold tracking-[0.3em] text-[#C6A15B]">{pt.n}</span>
              <h3 className="mt-4 font-display text-xl font-extrabold uppercase tracking-tight text-white">
                {pt.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{pt.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLanguage();
  const hw = t.how;
  return (
    <section className="bg-white py-20 md:py-32" data-testid="how-it-works">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHead eyebrow={hw.eyebrow} lines={[hw.heading]} />
        <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
          {hw.steps.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.1}>
              <div className="relative border-t border-ink/15 pt-8">
                <span className="absolute -top-[13px] left-0 bg-white pr-4 font-mono text-sm font-semibold tracking-[0.3em] text-[#C6A15B]">
                  {step.n}
                </span>
                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink sm:text-3xl">
                  {step.title}
                </h3>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-neutral-600">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeasonalTeaser() {
  const { t } = useLanguage();
  const s = t.seasonal;
  return (
    <section className="noise-bg bg-forest py-20 md:py-32" data-testid="seasonal-teaser">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Eyebrow dark>{s.eyebrow}</Eyebrow>
            <MaskedLines
              as="h2"
              lines={[s.line1, s.line2]}
              inView
              delay={0.1}
              className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
            />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <p className="max-w-md text-base leading-relaxed text-white/70">{s.intro}</p>
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {s.seasons.map((season, i) => (
            <Reveal key={season.id} delay={i * 0.08} className="bg-forest">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-forest-dark">
                <span className="font-mono text-[11px] font-semibold tracking-[0.3em] text-[#C6A15B]">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                  {season.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{season.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <ArrowButton to="/seasonal" variant="white" dataTestId="seasonal-teaser-button" className="mt-12">
            {s.cta}
          </ArrowButton>
        </Reveal>
      </div>
    </section>
  );
}

function Recurring() {
  const { t } = useLanguage();
  const r = t.recurring;
  return (
    <section className="bg-white py-20 md:py-32" data-testid="recurring">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
        <div className="order-2 lg:order-1 lg:col-span-6">
          <Eyebrow>{r.eyebrow}</Eyebrow>
          <MaskedLines
            as="h2"
            lines={[r.heading]}
            inView
            delay={0.1}
            className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-7 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{r.body}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <ArrowButton to="/contact?frequency=recurring" variant="outlineDark" dataTestId="recurring-cta-button" className="mt-9">
              {r.cta}
            </ArrowButton>
          </Reveal>
        </div>
        <Reveal className="order-1 lg:order-2 lg:col-span-6">
          <div className="overflow-hidden">
            <motion.img
              src={IMAGES.recurring}
              alt={r.alt}
              loading="lazy"
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, ease: EASE }}
              className="aspect-[4/3] w-full object-cover lg:aspect-[5/4]"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function AboutTeaser() {
  const { t } = useLanguage();
  const a = t.aboutHome;
  return (
    <section className="noise-bg bg-cream py-20 md:py-32" data-testid="about-teaser">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Eyebrow>{a.eyebrow}</Eyebrow>
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <MaskedLines
            as="h2"
            lines={[a.heading]}
            inView
            delay={0.1}
            className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink sm:text-4xl lg:col-span-6 lg:text-5xl"
          />
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.2}>
              <p className="text-base leading-relaxed text-neutral-600 sm:text-lg">{a.body}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <ArrowButton to="/about" variant="outlineDark" dataTestId="about-teaser-button" className="mt-8">
                {a.cta}
              </ArrowButton>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandMoment() {
  const { t } = useLanguage();
  const b = t.brand;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section ref={ref} className="relative flex min-h-[85svh] items-center overflow-hidden bg-ink" data-testid="brand-moment">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img src={IMAGES.brand} alt={b.alt} loading="lazy" className="h-[116%] w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-7xl px-6 py-24 sm:px-12 lg:px-20">
        <MaskedLines
          as="h2"
          lines={[b.line1, b.line2]}
          inView
          className="font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-white/90 sm:text-6xl lg:text-7xl"
        />
        <MaskedLines
          as="p"
          lines={[b.line3]}
          inView
          delay={0.25}
          className="mt-2 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-[#C6A15B] sm:text-6xl lg:text-7xl"
        />
        <Reveal delay={0.35}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-white/75 sm:text-lg">{b.sub}</p>
        </Reveal>
      </div>
    </section>
  );
}

function FaqItem({ item, open, onToggle, index }) {
  const id = `faq-item-${index}`;
  return (
    <div className="border-b border-ink/10">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        data-testid={`${id}-button`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className="font-display text-base font-bold tracking-tight text-ink sm:text-lg">{item.q}</span>
        <Plus
          className={`h-5 w-5 shrink-0 text-forest transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-6 text-sm leading-relaxed text-neutral-600 sm:text-base">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Faq() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-white py-20 md:py-32" data-testid="faq-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-4">
          <SectionHead eyebrow={t.faq.eyebrow} lines={[t.faq.heading]} />
        </div>
        <Reveal className="lg:col-span-8" delay={0.15}>
          <div className="border-t border-ink/10">
            {t.faq.items.map((item, i) => (
              <FaqItem key={i} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLanguage();
  useSeo('home');
  return (
    <main>
      <Hero />
      <ValueStrip />
      <Marquee items={t.marquee} />
      <ServicesSection />
      <FeaturedLawn />
      <Philosophy />
      <HowItWorks />
      <SeasonalTeaser />
      <Recurring />
      <AboutTeaser />
      <BrandMoment />
      <Faq />
      <FinalCTA />
    </main>
  );
}
